import React, { useState, useEffect } from 'react';
import QuestionBank from './components/QuestionBank';
import CreateAssessment from './components/CreateAssessment';
import AssessmentList from './components/AssessmentList';
import CarveAssessment from './components/CarveAssessment';
import useAssessmentManagementStore from '@/stores/AdminStore/AssessmentManagementStore';
import './AssessmentManagement.css';

type TabType = 'question-bank' | 'create' | 'list' | 'carve';

const AssessmentManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('question-bank');

  const {
    questions,
    assessments,
    availableQuestions,
    carveAssessmentSources,
    fetchQuestions,
    fetchAssessments,
    fetchAvailableQuestions,
    fetchCarveAssessmentSources,
  } = useAssessmentManagementStore();

  // Lazy load data based on active tab
  useEffect(() => {
    if (activeTab === 'question-bank' && !questions) {
      fetchQuestions();
    }
  }, [activeTab, questions, fetchQuestions]);

  useEffect(() => {
    if (activeTab === 'list' && !assessments) {
      fetchAssessments();
    }
  }, [activeTab, assessments, fetchAssessments]);

  useEffect(() => {
    if (activeTab === 'create' && !availableQuestions) {
      fetchAvailableQuestions();
    }
  }, [activeTab, availableQuestions, fetchAvailableQuestions]);

  useEffect(() => {
    if (activeTab === 'carve' && !carveAssessmentSources) {
      fetchCarveAssessmentSources();
    }
  }, [activeTab, carveAssessmentSources, fetchCarveAssessmentSources]);

  const tabs = [
    { id: 'question-bank' as TabType, label: 'Question Bank' },
    { id: 'create' as TabType, label: 'Create Assessment' },
    { id: 'list' as TabType, label: 'Assessment List' },
    { id: 'carve' as TabType, label: 'Carve Assessment' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'question-bank':
        return <QuestionBank />;
      case 'create':
        return <CreateAssessment />;
      case 'list':
        return <AssessmentList />;
      case 'carve':
        return <CarveAssessment />;
      default:
        return <QuestionBank />;
    }
  };

  return (
    <div className="assessment-management">
      <div className="page-header-wrapper">
        <div className="page-header-breadcrumb">
           Content Management › Assessments
        </div>
        <div className="page-header">
          <div>
            <h1 className='page-header-title'>Assessments</h1>
            <p className='page-header-subtitle'>Create and manage quizzes, tests, and surveys</p>
          </div>
          <div>
             {activeTab === 'create' && (
            <button className="create-btn">+ Create Assessment</button>
          )}
          </div>
        </div>
      </div>

      <div className="assessment-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`assessment-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="assessment-content">{renderTabContent()}</div>
    </div>
  );
};

export default AssessmentManagement;
